import React, { useState, useEffect } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { useNavigate, useParams } from 'react-router-dom';
import { MATH_DOMAINS } from '../../data/domains';
import { dataService } from '../../services/dataService';
import { Standard, Question, LearningPath, StandardInPath } from '../../data/types';
import PageContainer from '../layouts/PageContainer';

interface LearningPathDesignerScreenProps {
  mode: 'create' | 'manage';
}

const LearningPathDesignerScreen: React.FC<LearningPathDesignerScreenProps> = ({ mode }) => {
  const [pathName, setPathName] = useState('');
  const [pathDescription, setPathDescription] = useState('');
  const [pathDifficulty, setPathDifficulty] = useState<'Easy' | 'Medium' | 'Hard'>('Medium');
  const [selectedGrade, setSelectedGrade] = useState<string>('5');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [selectedStandard, setSelectedStandard] = useState<StandardInPath | null>(null);
  const [pathStandards, setPathStandards] = useState<StandardInPath[]>([]);
  const [draggedStandard, setDraggedStandard] = useState<Standard | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [showQuestionPreview, setShowQuestionPreview] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState<Question[]>([]);
  const [existingPaths, setExistingPaths] = useState<LearningPath[]>([]);
  const [standardsData, setStandardsData] = useState<Standard[]>([]);
  const [filteredStandards, setFilteredStandards] = useState<Standard[]>([]);
  const [currentPathId, setCurrentPathId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { pathId } = useParams<{ pathId: string }>();
  
  // Fetch learning paths and standards from dataService
  useEffect(() => {
    const fetchData = async () => {
      const paths = await dataService.getLearningPaths();
      const standards = await dataService.getStandards();
      setExistingPaths(paths);
      setStandardsData(standards);
      
      // If we're in edit mode and have a pathId, load the path data
      if (mode === 'manage' && pathId) {
        const pathToEdit = paths.find(path => path.id === pathId);
        if (pathToEdit) {
          setCurrentPathId(pathId);
          setPathName(pathToEdit.name);
          setPathDescription(pathToEdit.description);
          setPathDifficulty(pathToEdit.difficulty ?? 'Medium');
          setSelectedGrade(pathToEdit.grade);
          
          // Convert LearningPathStandard objects to StandardInPath format
          if (pathToEdit.standards && pathToEdit.standards.length > 0) {
            // Map each LearningPathStandard to a StandardInPath by finding the full standard data
            const pathStandardsData = pathToEdit.standards.map((pathStandard) => {
              // Find the full standard data using the standardId
              const standardData = standards.find(std => std.id === pathStandard.standardId);
              if (standardData) {
                // Create a StandardInPath object by combining the standard data with path-specific properties
                return {
                  ...standardData,
                  sequenceOrder: pathStandard.sequenceOrder,
                  settings: {
                    requiredAccuracy: 80,
                    minQuestions: 5,
                    allowSkip: false,
                    timeLimit: undefined
                  },
                  difficulty: pathToEdit.difficulty
                } as StandardInPath;
              }
              return null;
            }).filter(Boolean) as StandardInPath[];
            
            setPathStandards(pathStandardsData);
          }
        }
      }
    };
    
    fetchData();
  }, [mode, pathId]);
  
  // Filter standards based on search criteria
  useEffect(() => {
    const filterStandards = () => {
      if (standardsData.length === 0) return;
      
      const filtered = standardsData.filter((standard: Standard) => {
        // If selectedGrade is empty, show all grades (All option selected)
        const matchesGrade = selectedGrade === '' || standard.grade === selectedGrade;
        
        // Find domain ID for the selected domain name
        const selectedDomainId = selectedDomain ? 
          dataService.getDomainIdByName(selectedDomain) : '';
        
        const matchesDomain = !selectedDomain || standard.domainId === selectedDomainId;
        
        // Get domain name for search
        const domainName = dataService.getDomainNameById(standard.domainId) || '';
        
        const matchesSearch = !searchQuery || 
          standard.code.toLowerCase().includes(searchQuery.toLowerCase()) || 
          standard.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          domainName.toLowerCase().includes(searchQuery.toLowerCase());
        
        return matchesGrade && matchesDomain && matchesSearch;
      });
      
      setFilteredStandards(filtered);
    };
    
    filterStandards();
  }, [standardsData, selectedGrade, selectedDomain, searchQuery]);
  
  // Helper function to get domain ID by name
  const getDomainIdByName = (name: string): string => {
    // Use the dataService method
    return dataService.getDomainIdByName(name);
  };

  // Get questions for a standard using dataService
  const getMockQuestionsForStandard = async (standardId: string, difficulty: 'Easy' | 'Medium' | 'Hard'): Promise<Question[]> => {
    // Use the dataService method to get questions
    return await dataService.getQuestionsForStandard(standardId, difficulty);
  };
  
  // Handle viewing questions for a standard
  const handleViewQuestions = async (standard: StandardInPath) => {
    setSelectedStandard(standard);
    if (standard.difficulty) {
      const questions = await getMockQuestionsForStandard(standard.id, standard.difficulty);
      setSelectedQuestions(questions);
      setShowQuestionPreview(true);
    }
  };
  
  // Handle regenerating questions based on difficulty
  const handleRegenerateQuestions = () => {
    if (selectedStandard && selectedStandard.difficulty) {
      getMockQuestionsForStandard(selectedStandard.id, selectedStandard.difficulty)
        .then(questions => setSelectedQuestions(questions));
    }
  };
  
  // Handle removing a question
  const handleRemoveQuestion = (questionId: string) => {
    setSelectedQuestions(prevQuestions => 
      prevQuestions.filter(q => q.id !== questionId)
    );
  };
  
  // Handle updating settings for a standard in the path
  const handleUpdateSettings = (standardId: string, settings: any) => {
    // Create a new array with updated settings
    const updatedPathStandards = pathStandards.map(standard => 
      standard.id === standardId
        ? { ...standard, settings: { ...standard.settings, ...settings } }
        : standard
    );
    
    // Update the path standards state
    setPathStandards(updatedPathStandards);
    
    // Also update the selected standard to ensure UI immediately reflects changes
    if (selectedStandard && selectedStandard.id === standardId) {
      setSelectedStandard({
        ...selectedStandard,
        settings: { ...selectedStandard.settings, ...settings }
      });
    }
  };
  
  // Handle drag start for a standard
  const handleDragStart = (standard: Standard) => {
    setDraggedStandard(standard);
  };
  
  // Handle adding a standard to the path
  const handleAddToPath = (standard: Standard) => {
    // Check if standard is already in path
    if (pathStandards.some(s => s.id === standard.id)) return;
    
    // Create a StandardInPath object from the Standard
    const standardInPath: StandardInPath = {
      ...standard,
      sequenceOrder: pathStandards.length + 1,
      settings: {
        requiredAccuracy: 80,
        minQuestions: 5,
        allowSkip: false,
        timeLimit: undefined
      }
    };
    
    setPathStandards(prev => [...prev, standardInPath]);
  };
  
  // Handle removing a standard from the path
  const handleRemoveFromPath = (standardId: string) => {
    setPathStandards(prev => prev.filter(s => s.id !== standardId));
  };
  
  // Handle reordering standards in the path
  const handleReorder = (dragIndex: number, hoverIndex: number) => {
    setPathStandards(prev => {
      const newStandards = [...prev];
      const draggedItem = newStandards[dragIndex];
      
      // Remove the dragged item
      newStandards.splice(dragIndex, 1);
      // Insert it at the new position
      newStandards.splice(hoverIndex, 0, draggedItem);
      
      // Update sequence orders
      return newStandards.map((standard, index) => ({
        ...standard,
        sequenceOrder: index + 1
      }));
    });
  };
  
  // Handle selecting a standard
  const handleSelectStandard = (standard: StandardInPath) => {
    setSelectedStandard(standard);
  };
  
  // Handle saving the learning path
  const handleSavePath = () => {
    // Convert StandardInPath objects to LearningPathStandard format for saving
    const standardsToSave = pathStandards.map(standard => ({
      standardId: standard.id,
      sequenceOrder: standard.sequenceOrder
    }));

    // Determine if we're updating an existing path or creating a new one
    if (currentPathId && mode === 'manage') {
      // Update existing path
      const updatedPath = {
        id: currentPathId,
        name: pathName,
        description: pathDescription,
        difficulty: pathDifficulty,
        grade: selectedGrade,
        standards: standardsToSave,
        // Preserve other properties that shouldn't change during an update
        creatorId: '', // This would come from the existing path in a real app
        isPreset: false,
        assignedTo: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isShared: false,
        isDistrict: false
      };
      
      console.log('Updating existing path:', updatedPath);
      // In a real app, this would call an API update endpoint
      // dataService.updateLearningPath(updatedPath);
      alert('Learning path updated successfully!');
      navigate('/learning-paths/manage');
    } else {
      // Create new path
      const newPath = {
        id: `path-${Date.now()}`, // Generate a temporary ID (would be handled by backend in real app)
        name: pathName,
        description: pathDescription,
        difficulty: pathDifficulty,
        grade: selectedGrade,
        standards: standardsToSave,
        creatorId: '', // This would be the current user's ID in a real app
        isPreset: false,
        assignedTo: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isShared: false,
        isDistrict: false
      };
      
      console.log('Creating new path:', newPath);
      // In a real app, this would call an API create endpoint
      // dataService.createLearningPath(newPath);
      alert('Learning path created successfully!');
      navigate('/learning-paths/manage');
    }
  };
  
  // Get difficulty badge color
  const getDifficultyColor = (difficulty: 'Easy' | 'Medium' | 'Hard') => {
    switch (difficulty) {
      case 'Easy': return 'success';
      case 'Medium': return 'warning';
      case 'Hard': return 'error';
      default: return 'primary';
    }
  };

  // Handle navigation to assign path
  const handleAssignPath = (pathId: string) => {
    // Navigate to the assign path page with the selected path
    navigate(`/learning-paths/assign?pathId=${pathId}`);
  };

  // Handle navigation to edit path
  const handleEditPath = (path: LearningPath) => {
    // Set the path details and navigate to edit mode
    setPathName(path.name);
    setPathDescription(path.description);
    setPathDifficulty(path.difficulty ?? 'Medium');
    // In a real app, we would load the standards from the API
    
    // Navigate to edit page
    navigate(`/learning-paths/edit/${path.id}`);
  };

  // Handle add new path or from inventory
  const handleAddPath = (source: 'new' | 'inventory') => {
    if (source === 'new') {
      navigate('/learning-paths/create');
    } else {
      navigate('/learning-paths/inventory');
    }
  };

  return (
    <div className="pb-10">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>

          <p className="text-gray-600">
            {mode === 'create' 
              ? 'Create and customize learning paths for your students' 
              : currentPathId
                ? 'Edit and update your learning path'
                : 'View and edit your existing learning paths'}
          </p>
        </div>
        <div className="flex space-x-3 mt-4 md:mt-0">
          {/* Only show Add Path dropdown in manage mode and not in edit mode */}
          {mode === 'manage' && !currentPathId && (
            <div className="relative group">
              <Button 
                variant="primary" 
                className="flex items-center"
              >
                Add Path
                <svg 
                  className="w-4 h-4 ml-2" 
                  fill="currentColor" 
                  viewBox="0 0 20 20" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 01-1 1h-3a1 1 0 110-2h-3V9a1 1 0 011-1v-3a1 1 0 011 1h3a1 1 0 110 2h3V6a1 1 0 011-1z" 
                    clipRule="evenodd"
                  />
                </svg>
              </Button>
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 hidden group-hover:block">
                <div className="py-1">
                  <button 
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" 
                    onClick={() => handleAddPath('new')}
                  >
                    Create New Path
                  </button>
                  <button 
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" 
                    onClick={() => handleAddPath('inventory')}
                  >
                    Use Existing Path
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Show these buttons in both create mode and edit mode (when currentPathId exists) */}
          {(mode === 'create' || (mode === 'manage' && currentPathId)) && (
            <>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/learning-paths/manage')}
              >
                Cancel
              </Button>
              <Button variant="secondary">Preview</Button>
              <Button 
                variant="primary" 
                onClick={handleSavePath} 
                disabled={!pathName || pathStandards.length === 0}
              >
                {currentPathId ? 'Update Path' : 'Save Path'}
              </Button>
            </>
          )}
        </div>
      </div>
      
      {/* Path Selection (Only show in Manage mode and not in edit mode) */}
      {mode === 'manage' && !currentPathId && (
        <Card className="mb-6 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold">Your Learning Paths</h2>
            <div className="relative">
              <input
                type="text"
                placeholder="Search paths..."
                className="p-2 pl-8 border border-gray-300 rounded-md"
              />
              <svg 
                className="w-5 h-5 absolute left-2 top-2.5 text-gray-400" 
                fill="currentColor" 
                viewBox="0 0 20 20" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  fillRule="evenodd" 
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" 
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {existingPaths.map(path => (
              <div 
                key={path.id} 
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium">{path.name}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{path.description}</p>
                  </div>
                  <Badge variant={getDifficultyColor(path.difficulty ?? 'Medium')}>
                    {path.difficulty ?? 'Medium'}
                  </Badge>
                </div>
                <div className="mt-2 text-xs text-gray-500 flex items-center">
                  <span>{path.isShared ? 'Shared' : 'Private'}</span>
                  <span className="mx-2">•</span>
                  <span>Created {new Date(path.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="mt-4 flex space-x-2">
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={() => handleEditPath(path)}
                    className="flex-1"
                  >
                    Edit
                  </Button>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => handleAssignPath(path.id)}
                    className="flex-1"
                  >
                    Assign
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      
      {/* Learning Path Information - Compact Design */}
      <Card className="mb-4 p-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Column 1: Name and Difficulty */}
          <div className="flex flex-col justify-between">
            <div>
              <label htmlFor="pathName" className="block text-sm font-medium text-gray-700 mb-1">
                Learning Path Name*
              </label>
              <input
                type="text"
                id="pathName"
                value={pathName}
                onChange={(e) => setPathName(e.target.value)}
                className="input w-full"
                placeholder="Enter a name for this learning path"
              />
            </div>
            
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Path Difficulty*
              </label>
              <div className="flex space-x-2">
                {['Easy', 'Medium', 'Hard'].map((difficulty) => (
                  <button
                    key={difficulty}
                    type="button"
                    className={`flex-1 py-1 px-2 rounded-md text-xs font-medium ${
                      pathDifficulty === difficulty
                        ? difficulty === 'Easy' 
                          ? 'bg-green-100 text-green-800 border border-green-300'
                          : difficulty === 'Medium'
                          ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                          : 'bg-red-100 text-red-800 border border-red-300'
                        : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                    }`}
                    onClick={() => setPathDifficulty(difficulty as 'Easy' | 'Medium' | 'Hard')}
                  >
                    {difficulty}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Affects suggested questions for each standard
              </p>
            </div>
          </div>
          
          {/* Column 2: Description */}
          <div>
            <label htmlFor="pathDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="pathDescription"
              value={pathDescription}
              onChange={(e) => setPathDescription(e.target.value)}
              className="input w-full"
              placeholder="Describe what students will learn in this path"
              rows={3}
            />
          </div>
        </div>
      </Card>
      
      {/* Main Designer Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-9 gap-6">
        {/* Left Panel - Standards Library */}
        <div className="lg:col-span-3">
          <Card className="p-4">
            <h2 className="text-lg font-semibold mb-4">Standards Library</h2>
            
            {/* Filter Controls */}
            <div className="space-y-4 mb-4">
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                  Search Standards
                </label>
                <input
                  type="text"
                  id="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input w-full"
                  placeholder="Search by code or description"
                />
              </div>
              
              <div>
                <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-1">
                  Grade Level
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    key="All"
                    onClick={() => setSelectedGrade('')}
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedGrade === ''
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All
                  </button>
                  {dataService.getGrades().map(grade => (
                    <button
                      key={grade}
                      onClick={() => setSelectedGrade(grade)}
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        selectedGrade === grade
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {grade}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label htmlFor="domain" className="block text-sm font-medium text-gray-700 mb-1">
                  Domain
                </label>
                <select
                  id="domain"
                  value={selectedDomain || ''}
                  onChange={(e) => setSelectedDomain(e.target.value || null)}
                  className="input w-full"
                >
                  <option value="">All Domains</option>
                  {dataService.getDomains().map(domain => (
                    <option key={domain.id} value={domain.name}>{domain.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Standard Items List */}
            <div className="space-y-2 max-h-96 overflow-y-auto p-1">
              {filteredStandards.map((standard: Standard) => {
  const isSelected = pathStandards.some(s => s.id === standard.id);
  const domainName = dataService.getDomainNameById(standard.domainId) || '';
  return (
    <div
      key={standard.id}
      draggable
      onDragStart={() => handleDragStart(standard)}
      className={`border rounded-lg p-3 transition-colors flex flex-col justify-between min-h-[120px] ${
        isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start">
        <div className="flex-1 min-w-0">
          <span className="font-medium text-sm text-gray-900 truncate block" title={standard.code}>{standard.code}</span>
          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{standard.description}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {domainName && (
              <div className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">Domain: {domainName}</div>
            )}
            <div className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">Grade {standard.grade}</div>
          </div>
        </div>
        <div className="flex-shrink-0 ml-2 flex items-center">
          {isSelected ? (
            <span className="inline-flex items-center text-green-600" title="Already added">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </span>
          ) : (
            <button
              onClick={() => handleAddToPath(standard)}
              className="bg-primary text-white rounded-full p-1 hover:bg-primary-dark transition-colors"
              title="Add to path"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </button>
          )}
        </div>
      </div>

    </div>
  );
})}
              
              {filteredStandards.length === 0 && (
                <div className="text-center py-8 px-4">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No standards found</h3>
                  <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filters.</p>
                </div>
              )}
            </div>
            
            <div className="text-center mt-4 text-sm text-gray-500">
              <p>Click the + button or drag standards to add them to your learning path</p>
            </div>
          </Card>
        </div>
        
        {/* Center Panel - Canvas */}
        <div className="lg:col-span-4">
          <Card className="p-4 min-h-[600px]">
            <h2 className="text-lg font-semibold mb-4">Learning Path Sequence</h2>
            
            {/* Canvas Drop Area */}
            <div
              className={`border-2 border-dashed rounded-lg min-h-[500px] flex flex-col ${
                draggedStandard && !pathStandards.some(s => s.id === draggedStandard.id)
                  ? 'border-primary bg-primary-light/10'
                  : 'border-gray-300'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                if (draggedStandard && !pathStandards.some(s => s.id === draggedStandard.id)) {
                  // First-time drop to add to path
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (draggedStandard && !pathStandards.some(s => s.id === draggedStandard.id)) {
                  handleAddToPath(draggedStandard);
                }
              }}
            >
              {pathStandards.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No standards in path</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Drag standards from the library to add them to your learning path.
                  </p>
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  {pathStandards.map((standard: StandardInPath, index: number) => (
                    <React.Fragment key={standard.id}>
                      {/* Standard item with integrated drop zone */}
                      <div
                        className={`relative ${dragOverIndex === index ? 'mt-6' : 'mt-1'} p-4 border-2 rounded-lg bg-white hover:shadow-md transition-shadow ${
                          selectedStandard?.id === standard.id ? 'border-primary' : 'border-gray-200'
                        }`}
                        draggable
                        onDragStart={() => handleDragStart(standard)}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOverIndex(index);
                        }}
                        onDragLeave={(e) => {
                          // Only clear if we're not still over the element
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = e.clientX;
                          const y = e.clientY;
                          if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                            setDragOverIndex(null);
                          }
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (draggedStandard) {
                            const dragIndex = pathStandards.findIndex(s => s.id === draggedStandard.id);
                            if (dragIndex !== -1) {
                              handleReorder(dragIndex, index);
                            }
                          }
                        }}
                      >
                        {/* Visual indicator for drop target */}
                        {dragOverIndex === index && (
                          <div className="absolute -top-4 left-0 right-0 h-3 bg-primary rounded-full"></div>
                        )}
                        
                        {/* Standard item */}
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center">
                            <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold mr-2">
                              {index + 1}
                            </div>
                            <div className="font-medium">{standard.code}</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge variant={getDifficultyColor(standard.difficulty || 'Medium')}>
                              {standard.difficulty || 'Medium'}
                            </Badge>
                            <button
  onClick={(e) => {
    e.stopPropagation();
    handleRemoveFromPath(standard.id);
  }}
  className="text-gray-400 hover:text-red-500 rounded-full p-1 transition-colors"
  title="Remove from path"
>
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
</button>

                          </div>
                        </div>
                        <p className="text-sm text-gray-600">{standard.description}</p>
                        
                        {/* Standard settings summary with Configure button */}
                        <div className="flex flex-wrap items-center justify-between mt-3">
                          <div className="flex flex-wrap gap-2">
                            <div className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                              Accuracy: {standard.settings.requiredAccuracy}%
                            </div>
                            <div className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                              Min Questions: {standard.settings.minQuestions}
                            </div>
                            <div className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                              {standard.settings.allowSkip ? 'Skip Allowed' : 'No Skip'}
                            </div>
                          </div>
                          
                          {/* Configure Button */}
                          <button
                            className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded text-gray-700 flex items-center gap-1 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Type assertion to StandardInPath since we know it has the required properties
                              handleSelectStandard(standard as StandardInPath);
                            }}
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                            </svg>
                            Configure
                          </button>
                        </div>
                      </div>
                      
                      {/* Drop zone at the end */}
                      {index === pathStandards.length - 1 && (
                        <div
                          className={`mt-3 p-3 border-2 border-dashed rounded-lg transition-all duration-200 ${
                            dragOverIndex === index + 1 ? 'border-primary bg-primary-light/10' : 'border-gray-300'
                          }`}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setDragOverIndex(index + 1);
                          }}
                          onDragLeave={() => setDragOverIndex(null)}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (draggedStandard) {
                              const dragIndex = pathStandards.findIndex(s => s.id === draggedStandard.id);
                              if (dragIndex !== -1) {
                                handleReorder(dragIndex, index + 1);
                              }
                            }
                          }}
                        >
                          <div className="text-center text-gray-500 text-sm">
                            Drop here to place at the end
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
        
        {/* Right Panel - Details */}
        <div className="lg:col-span-2">
          <Card className="p-4 min-h-[600px]">
            <h2 className="text-lg font-semibold mb-4">Standard Details</h2>
            
            {selectedStandard ? (
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-start">
                    <h3 className="font-medium">{selectedStandard.code}</h3>
                    <Badge variant={getDifficultyColor(selectedStandard.difficulty || 'Medium')}>
                      {selectedStandard.difficulty || 'Medium'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{selectedStandard.description}</p>
                  <p className="text-xs text-gray-500 mt-1">{dataService.getDomainNameById(selectedStandard.domainId) || ''}</p>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-medium mb-2">Settings</h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Required Accuracy (%)
                      </label>
                      <input 
                        type="number" 
                        min="0"
                        max="100"
                        value={selectedStandard.settings.requiredAccuracy}
                        onChange={(e) => handleUpdateSettings(selectedStandard.id, {
                          ...selectedStandard.settings,
                          requiredAccuracy: parseInt(e.target.value)
                        })}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum Questions
                      </label>
                      <input 
                        type="number" 
                        min="1"
                        value={selectedStandard.settings.minQuestions}
                        onChange={(e) => handleUpdateSettings(selectedStandard.id, {
                          ...selectedStandard.settings,
                          minQuestions: parseInt(e.target.value)
                        })}
                        className="input w-full"
                      />
                    </div>
                    
                    <div className="flex items-center">
                      <input 
                        type="checkbox" 
                        id="allowSkip"
                        checked={selectedStandard.settings.allowSkip}
                        onChange={(e) => handleUpdateSettings(selectedStandard.id, {
                          ...selectedStandard.settings,
                          allowSkip: e.target.checked
                        })}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label htmlFor="allowSkip" className="ml-2 block text-sm text-gray-700">
                        Allow students to skip this standard
                      </label>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Time Limit (minutes, optional)
                      </label>
                      <input 
                        type="number" 
                        min="0"
                        value={selectedStandard.settings.timeLimit || ''}
                        onChange={(e) => handleUpdateSettings(selectedStandard.id, {
                          ...selectedStandard.settings,
                          timeLimit: e.target.value ? parseInt(e.target.value) : undefined
                        })}
                        className="input w-full"
                        placeholder="No time limit"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium">Questions</h4>
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => selectedStandard && handleViewQuestions(selectedStandard)}
                    >
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path>
                      </svg>
                      View Questions
                    </Button>
                  </div>
                  <p className="text-sm text-gray-600">
                    This standard has questions available for {selectedStandard.difficulty || 'Medium'} difficulty.
                  </p>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <Button 
                    variant="secondary" 
                    size="sm"
                    className="w-full"
                    onClick={() => setSelectedStandard(null)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No standard selected</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Click the Configure button on any standard to view and edit its details.
                </p>
              </div>
            )}
          </Card>
        </div>
      </div>
      
      {/* Question Preview Modal */}
      {showQuestionPreview && selectedStandard && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Questions for {selectedStandard.code}
                    </h3>
                    <div className="flex justify-between items-center mb-4">
                      <p className="text-sm text-gray-500">
                        Standard: {selectedStandard.description}
                      </p>
                      <Badge variant={getDifficultyColor(pathDifficulty)}>
                        {pathDifficulty} Difficulty
                      </Badge>
                    </div>
                    
                    <div className="space-y-4 mt-6 max-h-96 overflow-y-auto">
                      {selectedQuestions.map((question, index) => (
                        <div 
                          key={question.id} 
                          className="border border-gray-200 rounded-lg p-4 relative"
                        >
                          <button 
                            onClick={() => handleRemoveQuestion(question.id)}
                            className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                            </svg>
                          </button>
                          <div className="font-medium mb-3">
                            Question {index + 1}: {question.text}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                            {question.options && Array.isArray(question.options) && question.options.map((option, optionIndex) => (
                              <div 
                                key={optionIndex}
                                className={`border rounded-md p-2 ${
                                  question.correctAnswer === optionIndex 
                                    ? 'bg-green-50 border-green-300' 
                                    : 'bg-white border-gray-200'
                                }`}
                              >
                                <div className="flex items-center">
                                  <div className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded-full mr-2 text-sm">
                                    {String.fromCharCode(65 + optionIndex)}
                                  </div>
                                  <div>{typeof option === 'string' ? option : (option as any).text || option.toString()}</div>
                                  {question.correctAnswer === optionIndex && (
                                    <svg className="w-5 h-5 ml-2 text-green-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                                    </svg>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {selectedQuestions.length === 0 && (
                      <div className="text-center py-10">
                        <p className="text-gray-500">No questions selected for this standard</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <Button 
                  variant="primary" 
                  className="w-full sm:w-auto sm:ml-3" 
                  onClick={() => setShowQuestionPreview(false)}
                >
                  Done
                </Button>
                <Button 
                  variant="secondary" 
                  className="mt-3 w-full sm:mt-0 sm:w-auto" 
                  onClick={handleRegenerateQuestions}
                >
                  Regenerate Questions
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
  );
};

export default LearningPathDesignerScreen; 